import { useEffect, useMemo, useState } from "react";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
    Box,
    Button,
    Collapse,
    Divider,
    FormControl,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TableContainer,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { unwrapResult } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar";
import { FlexDiv } from "../../../app/Utils";
import { AddFabButton } from "../../../app/components/AddFabButton";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { loadAllImportedDocuments, ImportedDocument } from "./importedDocumentsApi";
import { useNavigate } from "react-router-dom";
import { Localized, useLocalization } from "@fluent/react";
import { StructureName } from "../../device/Structure";
import { ImportedDocumentStatusChip } from "./components/ImportedDocumentStatusChip";
import { formatCountryLabel } from "../../administration/companyProfile/countriesApi";
import { selectSettings } from "../../settings/settingsSlice";
import { GoToButton } from "../../../app/components/GoToButton";

type ViewMode = "grid" | "customer" | "countryTree";

const ALL_COUNTRIES = "__all_countries__";
const ALL_CUSTOMERS = "__all_customers__";
const NON_CUSTOMER = "__non_customer__";
const NO_COUNTRY = "__no_country__";

export const ImportedDocumentsList = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);

    const [rows, setRows] = useState<ImportedDocument[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [viewMode, setViewMode] = useState<ViewMode>("grid");
    const [selectedCountry, setSelectedCountry] = useState<string>(ALL_COUNTRIES);
    const [selectedCustomerKey, setSelectedCustomerKey] = useState<string>(ALL_CUSTOMERS);
    const [expandedCountries, setExpandedCountries] = useState<string[]>([]);

    useEffect(() => {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        dispatch(loadAllImportedDocuments())
            .then(unwrapResult)
            .then((data) => {
                setRows(data ?? []);
            })
            .catch((err) => {
                console.error(err);
                setIsError(true);
                setError(err);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch]);

    const reload = () => {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        dispatch(loadAllImportedDocuments())
            .then(unwrapResult)
            .then((data) => setRows(data ?? []))
            .catch((err) => {
                console.error(err);
                setIsError(true);
                setError(err);
            })
            .finally(() => setIsLoading(false));
    };

    const handleGoToClick = (id: any) => {
        navigate(`/imported-documents/edit/${id}`);
    };

    const handleOpenCreateForm = () => {
        navigate(`/supplier-new-import`);
    };

    const documentsCountLabel = (count: number) =>
        l10n.getString("imported-documents-documents-count", { count }, `${count} documents`);

    const countries = useMemo(() => {
        const unique = Array.from(
            new Set(
                rows
                    .map((row) => row.counterparty?.countryCode?.trim())
                    .filter((country): country is string => !!country)
            )
        ).sort();

        return unique;
    }, [rows]);

    const rowsByCountry = useMemo(() => {
        if (selectedCountry === ALL_COUNTRIES) {
            return rows;
        }

        return rows.filter((row) => (row.counterparty?.countryCode?.trim() || "") === selectedCountry);
    }, [rows, selectedCountry]);

    const customerBuckets = useMemo(() => {
        const buckets = new Map<string, { key: string; name: string; count: number }>();

        rowsByCountry.forEach((row) => {
            const publicId = row.counterparty?.publicId?.trim();
            const key = publicId || NON_CUSTOMER;
            const name = publicId
                ? (row.counterparty?.displayName?.trim() || row.relatedCompanyName || l10n.getString("imported-documents-unknown-customer", null, "Unknown customer"))
                : l10n.getString("imported-documents-non-customer", null, "Non customer");

            const existing = buckets.get(key);
            if (existing) {
                existing.count += 1;
            } else {
                buckets.set(key, { key, name, count: 1 });
            }
        });

        return Array.from(buckets.values()).sort((a, b) => {
            if (a.key === NON_CUSTOMER) return 1;
            if (b.key === NON_CUSTOMER) return -1;
            return a.name.localeCompare(b.name);
        });
    }, [rowsByCountry, l10n]);

    const countryTree = useMemo(() => {
        const buckets = new Map<string, {
            key: string;
            label: string;
            count: number;
            customers: Array<{ key: string; name: string; count: number }>;
        }>();

        rows.forEach((row) => {
            const countryCode = row.counterparty?.countryCode?.trim() || NO_COUNTRY;
            const countryLabel = countryCode === NO_COUNTRY
                ? l10n.getString("imported-documents-no-country", null, "_No country")
                : formatCountryLabel(countryCode, locale);
            const customerKey = row.counterparty?.publicId?.trim() || NON_CUSTOMER;
            const customerName = row.counterparty?.publicId?.trim()
                ? (row.counterparty?.displayName?.trim() || row.relatedCompanyName || l10n.getString("imported-documents-unknown-customer", null, "Unknown customer"))
                : l10n.getString("imported-documents-non-customer", null, "Non customer");

            const countryBucket = buckets.get(countryCode) ?? {
                key: countryCode,
                label: countryLabel,
                count: 0,
                customers: [],
            };

            countryBucket.count += 1;

            const customerBucket = countryBucket.customers.find((customer) => customer.key === customerKey);
            if (customerBucket) {
                customerBucket.count += 1;
            } else {
                countryBucket.customers.push({ key: customerKey, name: customerName, count: 1 });
            }

            buckets.set(countryCode, countryBucket);
        });

        return Array.from(buckets.values())
            .map((country) => ({
                ...country,
                customers: country.customers.sort((a, b) => {
                    if (a.key === NON_CUSTOMER) return 1;
                    if (b.key === NON_CUSTOMER) return -1;
                    return a.name.localeCompare(b.name);
                }),
            }))
            .sort((a, b) => {
                if (a.key === NO_COUNTRY) return 1;
                if (b.key === NO_COUNTRY) return -1;
                return a.label.localeCompare(b.label);
            });
    }, [rows, l10n, locale]);

    const visibleRows = useMemo(() => {
        if (selectedCustomerKey === ALL_CUSTOMERS) {
            return rowsByCountry;
        }

        if (selectedCustomerKey === NON_CUSTOMER) {
            return rowsByCountry.filter((row) => !row.counterparty?.publicId?.trim());
        }

        return rowsByCountry.filter((row) => (row.counterparty?.publicId?.trim() || "") === selectedCustomerKey);
    }, [rowsByCountry, selectedCustomerKey]);

    useEffect(() => {
        if (selectedCustomerKey === ALL_CUSTOMERS) {
            return;
        }

        const exists = customerBuckets.some((bucket) => bucket.key === selectedCustomerKey);
        if (!exists) {
            setSelectedCustomerKey(ALL_CUSTOMERS);
        }
    }, [customerBuckets, selectedCustomerKey]);

    useEffect(() => {
        if (countryTree.length === 0) {
            setExpandedCountries([]);
            return;
        }

        setExpandedCountries((prev) => {
            if (prev.length > 0) {
                return prev.filter((key) => countryTree.some((country) => country.key === key));
            }

            return countryTree.slice(0, 1).map((country) => country.key);
        });
    }, [countryTree]);

    const toggleCountryExpanded = (countryKey: string) => {
        setExpandedCountries((prev) => (
            prev.includes(countryKey)
                ? prev.filter((key) => key !== countryKey)
                : [...prev, countryKey]
        ));
    };

    const treeVisibleRows = useMemo(() => {
        if (selectedCountry === ALL_COUNTRIES) {
            return rows;
        }

        const countryRows = rows.filter((row) => (row.counterparty?.countryCode?.trim() || NO_COUNTRY) === selectedCountry);
        if (selectedCustomerKey === ALL_CUSTOMERS) {
            return countryRows;
        }

        if (selectedCustomerKey === NON_CUSTOMER) {
            return countryRows.filter((row) => !row.counterparty?.publicId?.trim());
        }

        return countryRows.filter((row) => (row.counterparty?.publicId?.trim() || "") === selectedCustomerKey);
    }, [rows, selectedCountry, selectedCustomerKey]);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'counterpartyDetails',
                headerName: l10n.getString('step-customer', null, '_Customer'),
                minWidth: 200,
                flex: 1,
                valueGetter: (_, row) => row.counterparty?.displayName || row.relatedCompanyName || l10n.getString("imported-documents-non-customer", null, "Non customer"),
            },
            {
                field: 'counterpartyCountry',
                headerName: l10n.getString('imported-documents-country', null, '_Country'),
                width: 190,
                valueGetter: (_, row) => formatCountryLabel(row.counterparty?.countryCode, locale),
            },
            {
                field: 'structureId',
                headerName: l10n.getString("structure", null, "_Structure"),
                flex: 1,
                minWidth: 200,
                renderCell: (params) => {
                    return (
                        <StructureName structureId={params.row.structureId} />
                    )
                }
            },
            {
                field: "createDate",
                headerName: l10n.getString("imported-documents-import-date", null, "Import date"),
                minWidth: 140,
                valueFormatter: (value) => {
                    if (!value) return "-";
                    return new Date(value as string).toLocaleDateString();
                }
            },
            {
                field: "createdByFullName",
                headerName: l10n.getString("imported-documents-imported-by", null, "_Imported by"),
                minWidth: 150,
                valueGetter: (_, row) => row.createdByFullName || row.createdBy || "-",
            },
            {
                field: 'title',
                headerName: l10n.getString("imported-documents-document-title", null, "_Document title"),
                minWidth: 300,
                flex: 1
            },
            {
                field: 'version',
                headerName: 'V',
                width: 20,
                valueGetter: (_, row) => {
                    return row.version
                }
            },
            // {
            //     field: "number",
            //     headerName: l10n.getString("urs-number", null, "_Numer URS"),
            //     flex: 1,
            //     minWidth: 160
            // },
            {
                field: "status",
                headerName: l10n.getString("status", null, "Status"),
                minWidth: 170,
                renderCell: (params) => {
                    return <ImportedDocumentStatusChip status={params.row.status} />;
                }
            },
            {
                field: 'actions',
                type: 'actions',
                width: 60,
                align: 'left',
                headerAlign: 'left',
                renderCell: (params) => (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <GoToButton onClick={() => handleGoToClick(params.row.id)} />
                    </Box>
                )
            }
        ],
        [l10n, locale]
    );

    if (isError) {
        return <ErrorContainer error={error} />;
    }

    if (isLoading) {
        return <LoadingContainer />;
    }

    // Prevent the grid's column widths from setting the page's intrinsic flex width.
    return (
        <TableContainer component={Paper} sx={{
            width: 0,
            minWidth: "100%",
            height: "calc(100dvh - 112px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: "grey.100",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <Localized id="documents-imported-title">
                        <span>_Imported documents</span>
                    </Localized>
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={viewMode}
                    onChange={(_, value: ViewMode | null) => {
                        if (value) {
                            setViewMode(value);
                        }
                    }}
                >
                    <Tooltip title={l10n.getString("imported-documents-view-table", null, "_Table")}>
                        <ToggleButton value="grid" aria-label={l10n.getString("imported-documents-view-table", null, "Table")}>
                            <ViewListIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title={l10n.getString("imported-documents-view-customer", null, "By customer")}>
                        <ToggleButton value="customer" aria-label={l10n.getString("imported-documents-view-customer", null, "By customer")}>
                            <GroupsIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title={l10n.getString("imported-documents-view-country-tree", null, "Country tree")}>
                        <ToggleButton value="countryTree" aria-label={l10n.getString("imported-documents-view-country-tree", null, "Country tree")}>
                            <AccountTreeIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </Stack>

            {viewMode === "grid" ? (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    sx={{ flex: "1 1 0", minHeight: 0, minWidth: 0 }}
                    slots={{ toolbar: QuickSearchToolbar }}
                    onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                    initialState={{
                        sorting: {
                            sortModel: [
                                { field: "createDate", sort: "desc" }
                            ]
                        }
                    }}
                />
            ) : viewMode === "customer" ? (
                <Box sx={{ display: "flex", flex: "1 1 0", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
                    <Paper variant="outlined" square sx={{ width: 260, flexShrink: 0, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <Stack spacing={1} sx={{ p: 1.5 }}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="imported-docs-country-filter-label">
                                    {l10n.getString("imported-documents-filter-country", null, "Country")}
                                </InputLabel>
                                <Select
                                    labelId="imported-docs-country-filter-label"
                                    label={l10n.getString("imported-documents-filter-country", null, "Country")}
                                    value={selectedCountry}
                                    onChange={(e) => {
                                        setSelectedCountry(e.target.value);
                                        setSelectedCustomerKey(ALL_CUSTOMERS);
                                    }}
                                >
                                    <MenuItem value={ALL_COUNTRIES}>{l10n.getString("imported-documents-all-countries", null, "All countries")}</MenuItem>
                                    {countries.map((country) => (
                                        <MenuItem key={country} value={country}>{formatCountryLabel(country, locale)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Divider />

                        <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", p: 0 }}>
                            <ListItemButton
                                selected={selectedCustomerKey === ALL_CUSTOMERS}
                                onClick={() => setSelectedCustomerKey(ALL_CUSTOMERS)}
                            >
                                <ListItemText
                                    primary={l10n.getString("imported-documents-all-customers", null, "All customers")}
                                    secondary={documentsCountLabel(rowsByCountry.length)}
                                />
                            </ListItemButton>

                            {customerBuckets.map((bucket) => (
                                <ListItemButton
                                    key={bucket.key}
                                    selected={selectedCustomerKey === bucket.key}
                                    onClick={() => setSelectedCustomerKey(bucket.key)}
                                >
                                    <ListItemText
                                        primary={bucket.name}
                                        secondary={documentsCountLabel(bucket.count)}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>

                    <Box sx={{ flex: "1 1 0", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
                        <DataGrid
                            sx={{ width: "100%", height: "100%", minWidth: 0 }}
                            rows={visibleRows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            slots={{ toolbar: QuickSearchToolbar }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                            initialState={{
                                sorting: {
                                    sortModel: [
                                        { field: "createDate", sort: "desc" }
                                    ]
                                }
                            }}
                        />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flex: "1 1 0", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
                    <Paper variant="outlined" square sx={{ width: 260, flexShrink: 0, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", p: 0 }}>
                            <ListItemButton
                                selected={selectedCountry === ALL_COUNTRIES && selectedCustomerKey === ALL_CUSTOMERS}
                                onClick={() => {
                                    setSelectedCountry(ALL_COUNTRIES);
                                    setSelectedCustomerKey(ALL_CUSTOMERS);
                                }}
                            >
                                <ListItemText
                                    primary={l10n.getString("imported-documents-all-documents", null, "All documents")}
                                    secondary={documentsCountLabel(rows.length)}
                                />
                            </ListItemButton>

                            {countryTree.map((country) => {
                                const expanded = expandedCountries.includes(country.key);
                                const isCountrySelected = selectedCountry === country.key && selectedCustomerKey === ALL_CUSTOMERS;

                                return (
                                    <Box key={country.key}>
                                        <ListItemButton
                                            selected={isCountrySelected}
                                            onClick={() => {
                                                setSelectedCountry(country.key);
                                                setSelectedCustomerKey(ALL_CUSTOMERS);
                                            }}
                                        >
                                            <Box
                                                sx={{ display: "flex", alignItems: "center", mr: 1 }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleCountryExpanded(country.key);
                                                }}
                                            >
                                                {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                            </Box>
                                            <ListItemText
                                                primary={country.label}
                                                secondary={documentsCountLabel(country.count)}
                                            />
                                        </ListItemButton>

                                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                                            <List disablePadding dense>
                                                {country.customers.map((customer) => (
                                                    <ListItemButton
                                                        key={`${country.key}-${customer.key}`}
                                                        selected={selectedCountry === country.key && selectedCustomerKey === customer.key}
                                                        onClick={() => {
                                                            setSelectedCountry(country.key);
                                                            setSelectedCustomerKey(customer.key);
                                                        }}
                                                        sx={{ pl: 5 }}
                                                    >
                                                        <ListItemText
                                                            primary={customer.name}
                                                            secondary={documentsCountLabel(customer.count)}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </List>
                    </Paper>

                    <Box sx={{ flex: "1 1 0", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
                        <DataGrid
                            sx={{ width: "100%", height: "100%", minWidth: 0 }}
                            rows={treeVisibleRows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            slots={{ toolbar: QuickSearchToolbar }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                            initialState={{
                                sorting: {
                                    sortModel: [
                                        { field: "createDate", sort: "desc" }
                                    ]
                                }
                            }}
                        />
                    </Box>
                </Box>
            )}

            <FlexDiv style={{ flexShrink: 0 }}>
                <FlexDiv>
                    <AddFabButton onClick={handleOpenCreateForm} />
                </FlexDiv>
            </FlexDiv>
        </TableContainer>
    );
};
